import { HttpErrorResponse, HttpResponse } from "@angular/common/http";
import { Component, Input, OnInit } from "@angular/core";
import { Observable } from "rxjs";
import { finalize } from "rxjs/operators";

import { AuthService } from "src/app/auth/auth.service";
import { DisputeCaseType } from "src/app/shared/models/dispute-resolution/dispute-base-models";
import { AreaOfConcernPermissions } from "src/app/shared/permissions/area-of-concern-permissions";
import { DueProcessPermissions } from "src/app/shared/permissions/due-process-permissions";
import { FacilitationPermissions } from "src/app/shared/permissions/facilitation-permissions";
import { MediationPermissions } from "src/app/shared/permissions/mediation-permissions";
import { ParentEngagementPermissions } from "src/app/shared/permissions/parent-engagement-permissions";
import { StateComplaintPermissions } from "src/app/shared/permissions/state-complaint-permissions";
import { DisputeExportService } from "src/app/shared/services/dispute-resolution/dispute-export.service";
import { NotificationService } from "src/app/shared/services/notification.service";
import {
  downloadBlob,
  filenameFromContentDisposition,
} from "src/app/shared/utilities/blob-utils";

/**
 * The Export button shared by every dispute type, in both of its placements.
 *
 * Two modes, inferred from the inputs rather than configured:
 * - **single** — `caseId` is set; exports that one case.
 * - **list** — `caseIdsResolver` is set; exports whatever the caller's grid currently shows.
 *
 * List mode takes a callback rather than an id array on purpose. The set changes as the user types
 * in the search box and re-sorts the grid, and it is only meaningful at the moment of the click; a
 * bound array would either be recomputed on every change-detection pass or go stale.
 *
 * The permission check here controls whether the button is *visible*. It is not the security
 * boundary — both endpoints carry `[NeedsPermission(...Export)]` and an AU-scoped policy — but a
 * button that appears and then 403s is a worse experience than one that never appears.
 */
@Component({
  selector: "app-dispute-export-button",
  templateUrl: "./dispute-export-button.component.html",
  styleUrls: ["./dispute-export-button.component.scss"],
  standalone: false,
})
export class DisputeExportButtonComponent implements OnInit {
  @Input() caseType!: DisputeCaseType;

  /** Single-case mode: the case to export. */
  @Input() caseId?: string;

  /** Single-case mode: used for the download name if the server sends no `Content-Disposition`. */
  @Input() caseNumber?: string;

  /** List mode: resolves the ids to export, called on click. */
  @Input() caseIdsResolver?: () => string[];

  @Input() label = "Export";

  canExport = false;
  exporting = false;

  /**
   * Announced through an `aria-live` region. A file download produces no focus change and no visible
   * page update, so without this the whole interaction is silent for screen-reader users.
   */
  statusMessage = "";

  constructor(
    private disputeExportService: DisputeExportService,
    private authService: AuthService,
    private notificationService: NotificationService
  ) {}

  ngOnInit(): void {
    // Permission alone. `DisputeExportService.supports()` used to guard this too, but it maps all
    // seven types, so the only value it ever excluded was `DisputeCaseType.None` — which
    // hasExportPermission()'s `default:` already returns false for. Keeping both made the gate look
    // like it varied by type when it does not: every type's button is granted the same way.
    this.canExport = this.hasExportPermission();
  }

  get isSingleCase(): boolean {
    return !!this.caseId;
  }

  onExport(): void {
    if (this.exporting) return;

    const request = this.isSingleCase ? this.singleCaseRequest() : this.listRequest();
    if (!request) return;

    this.exporting = true;
    this.statusMessage = "Preparing export, please wait.";

    request
      .pipe(finalize(() => (this.exporting = false)))
      .subscribe({
        next: (response) => this.save(response),
        error: (error: HttpErrorResponse) => this.reportFailure(error),
      });
  }

  /**
   * Two of the failures reachable here are permanent, not transient: the server caps an oversized batch
   * rather than truncating it (a truncated workbook is indistinguishable from a complete one once it
   * leaves the app), and an export can be refused outright. Telling the user to "try again" in either
   * case sends them into a loop that cannot succeed, so the status code is what picks the message.
   *
   * The server's own explanation is more specific than anything that can be written here — it names the
   * batch cap, for instance — but `responseType: "blob"` means it arrives as a Blob rather than parsed
   * JSON, so it has to be read back out asynchronously before it can be shown.
   */
  private async reportFailure(error: HttpErrorResponse): Promise<void> {
    const fallback =
      error.status === 403
        ? "You do not have permission to export these cases."
        : error.status === 400 || error.status === 413
          ? "That is too many cases to export at once. Narrow the search and try again."
          : error.status === 404
            ? "That case is no longer available to export."
            : "Unable to build the export. Please try again.";

    const message = (await this.serverMessage(error)) || fallback;

    // Announce the same distinction the dialog makes, rather than a bare "Export failed."
    this.statusMessage = `Export failed. ${message}`;
    this.notificationService.alert(message, "Error");
  }

  /** Reads a ProblemDetails/plain-text body back out of the error Blob. Never throws. */
  private async serverMessage(error: HttpErrorResponse): Promise<string | null> {
    const body: unknown = error.error;
    if (!(body instanceof Blob) || body.size === 0) return null;

    try {
      const text = (await body.text()).trim();
      if (!text) return null;

      if (text.startsWith("{")) {
        const parsed = JSON.parse(text) as { detail?: string; title?: string };
        return parsed.detail || parsed.title || null;
      }

      // A bare string response arrives JSON-quoted; ASP.NET's BadRequest(string) does this.
      return text.startsWith('"') && text.endsWith('"') ? text.slice(1, -1) : text;
    } catch {
      // An unreadable or non-JSON body is not worth surfacing — the caller falls back.
      return null;
    }
  }

  private singleCaseRequest(): Observable<HttpResponse<Blob>> {
    return this.disputeExportService.exportCase(this.caseType, this.caseId as string);
  }

  private listRequest(): Observable<HttpResponse<Blob>> | null {
    const caseIds = this.caseIdsResolver ? this.caseIdsResolver() : [];

    if (!caseIds.length) {
      // Deliberately not a disabled button: a disabled control cannot show a tooltip explaining
      // why (Material sets pointer-events: none on it), so the reason would be invisible.
      this.statusMessage = "There are no cases to export.";
      this.notificationService.alert(
        "There are no cases to export. Adjust the search and try again."
      );
      return null;
    }

    return this.disputeExportService.exportList(this.caseType, caseIds);
  }

  private save(response: HttpResponse<Blob>): void {
    if (!response.body || response.body.size === 0) {
      this.statusMessage = "Export failed.";
      this.notificationService.alert("The export came back empty.", "Error");
      return;
    }

    const fileName = filenameFromContentDisposition(response, this.fallbackFileName());
    downloadBlob(response.body, fileName);
    this.statusMessage = `Export downloaded: ${fileName}`;
  }

  private fallbackFileName(): string {
    if (this.isSingleCase) {
      return `${this.caseNumber || this.caseId}-export.xlsx`;
    }

    const stamp = new Date().toISOString().slice(0, 10);
    return `${DisputeCaseType[this.caseType] ?? "DisputeCase"}-export-${stamp}.xlsx`;
  }

  /**
   * Mirrors the per-type permission switch in `LinkedCasesComponent` — `[NeedsPermission]` and
   * `[Authorize]` bind at compile time, so a component driven by a runtime `DisputeCaseType` has to
   * resolve the category itself.
   */
  private hasExportPermission(): boolean {
    switch (this.caseType) {
      case DisputeCaseType.StateComplaint:
        return this.authService.getPermission(StateComplaintPermissions.Export);
      case DisputeCaseType.DueProcess:
        return this.authService.getPermission(DueProcessPermissions.Export);
      case DisputeCaseType.Mediation:
        return this.authService.getPermission(MediationPermissions.Export);
      case DisputeCaseType.IEPFacilitation:
        return this.authService.getPermission(FacilitationPermissions.Export);
      case DisputeCaseType.AreaOfConcern:
        return this.authService.getPermission(AreaOfConcernPermissions.Export);
      // Early Dispute Resolution shares Parent Engagement's permission category.
      case DisputeCaseType.ParentEngagement:
      case DisputeCaseType.EarlyDisputeResolution:
        return this.authService.getPermission(ParentEngagementPermissions.Export);
      default:
        return false;
    }
  }
}
